import { useEffect, useCallback, useRef, useMemo } from 'react'
import {
  useAuthStore,
  useRestaurantStore,
  useCartStore,
  useDeliveryStore,
  useOrderStore,
  CartSyncService,
  CartSyncContext,
  buildCartItemsFromDraft,
  toPersistedAddressId,
} from '@restaurant/core'
import type { DraftOrder, DraftOrderItem, MenuItem } from '@restaurant/core'
import { apiClient, isAuthExpiredError } from '@restaurant/api-client'
import { WebPlatformService } from '@restaurant/viewmodels'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'


/**
 * useCartSync Hook
 *
 * Syncs cart state with the backend for cross-device persistence.
 * When a user is authenticated, cart changes are persisted to the backend
 * as a draft order (status: newOrder), allowing them to resume their cart
 * on any device.
 *
 * Sync Strategy:
 * - On login/app load: Fetch remote draft and merge with local cart
 * - On add item: Create draft if needed, then add item
 * - On remove/update: Sync changes to existing draft
 * - On clear: Delete draft from backend
 *
 * Error Handling:
 * - All sync operations are non-blocking (optimistic updates)
 * - Errors are logged but don't block the UX
 * - Cart remains functional even when offline
 *
 * Architecture:
 * - Uses CartSyncService from @restaurant/core for orchestration
 * - Data transformation handled by cart-transformations utility
 * - All API calls go through OrderService for validation/logging
 */
export function useCartSync() {
  const { user, isAuthenticated } = useAuthStore()
  const { currentRestaurant, currentLocation, categories, isDebugMode } = useRestaurantStore()
  const orderType = useOrderStore((s) => s.orderType)
  // Strip non-persisted ids (guest 'temp-address' sentinel, raw Google place_id)
  // so they never reach order-create / draft-metadata sync (backend 400s them).
  const rawDeliveryAddressId = useDeliveryStore((s) => s.currentAddress?.id)
  const deliveryAddressId = toPersistedAddressId(rawDeliveryAddressId)
  const {
    items,
    // draftOrderId is destructured here as a reactive dependency for the
    // restoration useEffect, not for use in syncFromBackend (which reads it
    // fresh from getState() to avoid stale closures).
    draftOrderId,
    setSyncCallbacks,
    setDraftOrderId,
    setSyncing,
    setSyncError,
    getPriceBreakdown,
    setTaxRate,
    restoreFromDraft,
  } = useCartStore()

  // Track if we've already synced on this session
  const hasSyncedRef = useRef(false)
  // Stores a fetched DraftOrder when local cart is empty, so restoration can be
  // deferred until restaurantStore.categories are available.
  const pendingRestoreDraftRef = useRef<DraftOrder | null>(null)
  // One-shot guard: restoration fires at most once per session.
  const hasRestoredRef = useRef(false)
  // Stable platform service instance for programmatic toast notifications.
  // useMemo with [] ensures single construction without per-render guard checks.
  const platformService = useMemo(() => new WebPlatformService(), [])

  // Get the merchant ID from location or restaurant POS integration.
  // For sandbox/debug mode the store overrides merchantId with the sandbox
  // Clover merchant, but order creation needs the REAL merchant ID so that
  // the FK to the restaurants table resolves. originalMerchantId is set by
  // the restaurant store when the sandbox override is applied.
  // Priority: originalMerchantId (real) > merchantId > first location > posIntegration
  const merchantId =
    currentLocation?.originalMerchantId ||
    currentLocation?.merchantId ||
    currentRestaurant?.locations?.[0]?.originalMerchantId ||
    currentRestaurant?.locations?.[0]?.merchantId ||
    currentRestaurant?.posIntegration?.merchantId

  // Create CartSyncService instance with proper dependencies
  const cartSyncService = useMemo(() => {
    return new CartSyncService({
      apiClient,
      userId: user?.id,
    })
  }, [user?.id])

  // Create sync context from current state
  const createSyncContext = useCallback((): CartSyncContext | null => {
    if (!user?.id || !currentRestaurant?.id || !merchantId) {
      return null
    }

    return {
      userId: user.id,
      businessId: currentRestaurant.id,
      merchantId,
      platform: 'Web',
      taxRate: currentLocation?.taxRate || currentRestaurant?.taxRate || 0.0875,
      isDelivery: orderType === 'Delivery',
      environment: isDebugMode ? 'dev' : 'prod',
      deliveryAddressId,
    }
  }, [user?.id, currentRestaurant?.id, currentRestaurant?.taxRate, merchantId, isDebugMode, deliveryAddressId, orderType])

  // Sync all local cart items to the backend as a new draft order.
  // Used both when no remote draft exists and on 404 recovery.
  const syncLocalItemsToBackend = useCallback(async (localItems: typeof items, label: string) => {
    const context = createSyncContext()
    if (!context) return

    let currentDraftId: string | null = null

    for (const item of localItems) {
      const breakdown = getPriceBreakdown()
      const priceBreakdown = {
        subtotal: breakdown.subtotal,
        deliveryFee: breakdown.deliveryFee,
        feesAndTaxes: breakdown.tax + (breakdown.serviceFee ?? 0),
        driverTip: breakdown.tip,
      }

      try {
        const newDraftId: string | null = await cartSyncService.syncAddItem(
          currentDraftId,
          context,
          item,
          priceBreakdown
        )

        if (newDraftId) {
          currentDraftId = newDraftId
        }
      } catch (error) {
        if (isAuthExpiredError(error)) {
          // Auth refresh already failed in the api-client interceptor; user is logged out.
          // Don't escalate to error — the auth subsystem handles recovery.
          safeGetLogger()?.warn('Cart item sync failed — auth expired', { domain: 'sync' })
          return
        }
        safeGetLogger()?.error('Cart item sync failed', { domain: 'sync', error: String(error) })
      }
    }

    if (currentDraftId) {
      safeGetLogger()?.sync('draft_created', { draftOrderId: currentDraftId, label })
      setDraftOrderId(currentDraftId)
    }
  }, [cartSyncService, createSyncContext, getPriceBreakdown, setDraftOrderId])

  // Fetch and sync remote draft order on login/app load
  const syncFromBackend = useCallback(async () => {
    if (!isAuthenticated || !user?.id || !currentRestaurant?.id) {
      return
    }

    safeGetLogger()?.sync('sync_started', { source: 'backend' })
    safeGetLogger()?.debug('Current draftOrderId from localStorage', { domain: 'sync', draftOrderId })
    setSyncing(true)
    setSyncError(null)

    try {
      const remoteDraft = await cartSyncService.fetchUserDraft(user.id, currentRestaurant.id)

      // Read items fresh from the store to avoid stale closure values.
      const currentItems = useCartStore.getState().items
      const currentDraftOrderId = useCartStore.getState().draftOrderId

      if (!remoteDraft) {
        safeGetLogger()?.debug('No remote draft order found', { domain: 'sync' })

        // If we have a stale draftOrderId in localStorage, clear it
        if (currentDraftOrderId) {
          safeGetLogger()?.debug('Clearing stale draftOrderId', { domain: 'sync', draftOrderId: currentDraftOrderId })
          setDraftOrderId(null)
        }

        // If we have local items but no remote draft, sync them immediately
        if (currentItems.length > 0) {
          safeGetLogger()?.debug('Local cart has items, syncing to backend immediately', { domain: 'sync' })
          await syncLocalItemsToBackend(currentItems, 'login')
        }
        return
      }

      safeGetLogger()?.sync('draft_found', { orderId: remoteDraft.orderId })

      // If local cart is empty, queue draft for restoration once categories load.
      // Local-wins rule: if the local cart already has items, skip restoration.
      if (currentItems.length === 0) {
        safeGetLogger()?.debug('Local cart empty, queuing draft for restoration', { domain: 'sync' })
        setDraftOrderId(remoteDraft.orderId)
        // Only queue if the draft actually has items worth restoring
        if (remoteDraft.items && remoteDraft.items.length > 0) {
          pendingRestoreDraftRef.current = remoteDraft
        }
      } else if (currentItems.length > 0 && !currentDraftOrderId) {
        // Local has items but no draft ID - set the remote draft ID
        safeGetLogger()?.debug('Setting draft order ID from remote', { domain: 'sync' })
        setDraftOrderId(remoteDraft.orderId)
      } else {
        // Both exist — adopt remote draft ID, keep local cart items (local-wins)
        safeGetLogger()?.debug('Both local and remote exist, using remote draft ID', { domain: 'sync' })
        setDraftOrderId(remoteDraft.orderId)
      }
    } catch (error: unknown) {
      const err = error as { statusCode?: number; message?: string } | null | undefined
      // 404 means no orders exist, which is fine - clear any stale draftOrderId and sync local items
      if (err?.statusCode === 404) {
        const currentDraftOrderId = useCartStore.getState().draftOrderId
        const currentItems = useCartStore.getState().items

        if (currentDraftOrderId) {
          safeGetLogger()?.debug('No orders found (404), clearing stale draftOrderId', { domain: 'sync', draftOrderId: currentDraftOrderId })
          setDraftOrderId(null)
        }

        // Sync local items immediately if we have any
        if (currentItems.length > 0) {
          safeGetLogger()?.debug('Local cart has items after 404, syncing to backend immediately', { domain: 'sync' })
          await syncLocalItemsToBackend(currentItems, '404 recovery')
        }
      } else if (isAuthExpiredError(error)) {
        // Auth refresh already failed; user is logged out. Don't surface a sync error
        // to the UI — the auth flow will redirect/clear state.
        safeGetLogger()?.warn('Backend sync failed — auth expired', { domain: 'sync' })
      } else {
        safeGetLogger()?.error('Backend sync failed', { domain: 'sync', error: String(error) })
        setSyncError(err?.message || 'Failed to sync cart')
      }
    } finally {
      setSyncing(false)
    }
  }, [
    isAuthenticated,
    user?.id,
    currentRestaurant?.id,
    cartSyncService,
    syncLocalItemsToBackend,
    setSyncing,
    setSyncError,
    setDraftOrderId,
  ])

  // Set up sync callbacks when authenticated
  useEffect(() => {
    if (isShowcase()) return
    if (isAuthenticated && user?.id && currentRestaurant?.id) {
      const context = createSyncContext()

      if (!context) {
        safeGetLogger()?.debug('No merchant ID available, skipping sync setup', { domain: 'sync' })
        safeGetLogger()?.debug('Sync setup skipped', { domain: 'sync', currentLocation, locations: currentRestaurant?.locations, posIntegration: currentRestaurant?.posIntegration })
        setSyncCallbacks(null)
        return
      }

      safeGetLogger()?.debug('Setting up sync callbacks', { domain: 'sync' })
      const callbacks = cartSyncService.createSyncCallbacks(context, {
        onDraftOrderIdChange: (id: string | null) => {
          setDraftOrderId(id)
        },
        getPriceBreakdown: () => {
          const breakdown = getPriceBreakdown()
          return {
            subtotal: breakdown.subtotal,
            tax: breakdown.tax,
            serviceFee: breakdown.serviceFee ?? 0,
            deliveryFee: breakdown.deliveryFee,
            tip: breakdown.tip,
          }
        },
      })

      setSyncCallbacks(callbacks)
    } else {
      // Clear callbacks when not authenticated
      setSyncCallbacks(null)
    }

    return () => {
      setSyncCallbacks(null)
    }
  }, [
    isAuthenticated,
    user?.id,
    currentRestaurant?.id,
    currentRestaurant?.locations,
    currentRestaurant?.posIntegration,
    currentLocation,
    merchantId,
    cartSyncService,
    createSyncContext,
    setSyncCallbacks,
    setDraftOrderId,
    getPriceBreakdown,
  ])

  // Keep draft order metadata in sync when delivery mode, store location,
  // or delivery address changes while a draft already exists.
  const prevMetaRef = useRef<{ merchantId?: string; orderType?: string; deliveryAddressId?: string }>({})
  useEffect(() => {
    if (isShowcase()) return
    const currentDraftId = useCartStore.getState().draftOrderId
    if (!isAuthenticated || !currentDraftId || !merchantId) return

    const prev = prevMetaRef.current
    const changed =
      (prev.merchantId !== undefined && prev.merchantId !== merchantId) ||
      (prev.orderType !== undefined && prev.orderType !== orderType) ||
      (prev.deliveryAddressId !== undefined && prev.deliveryAddressId !== deliveryAddressId)

    // Always update the ref to track current values
    prevMetaRef.current = { merchantId, orderType, deliveryAddressId }

    if (!changed) return

    const context = createSyncContext()
    if (!context) return

    const breakdown = getPriceBreakdown()
    const priceBreakdown = {
      subtotal: breakdown.subtotal,
      deliveryFee: breakdown.deliveryFee,
      feesAndTaxes: breakdown.tax + (breakdown.serviceFee ?? 0),
      driverTip: breakdown.tip,
    }

    cartSyncService.syncDraftMetadata(currentDraftId, context, priceBreakdown).catch((error) => {
      const errorType = (error as { type?: string })?.type
      // Pull request/response context off the wrapped axios error when available.
      // The api-client preserves the original at .originalError (see client.ts:148-153).
      // Don't log originalError in full — it carries axios config (auth headers, baseURL).
      const original = (error as {
        originalError?: {
          response?: { status?: number; data?: unknown }
          config?: { url?: string; method?: string }
        }
      })?.originalError
      const requestPath = original?.config?.url
      const status = original?.response?.status
      const responseBody = original?.response?.data

      if (isAuthExpiredError(error)) {
        // Auth refresh already failed; the api-client logged the user out. The next
        // cart op or auth recovery will retry — return to skip the stale-draft check below.
        safeGetLogger()?.warn('Draft sync failed — auth expired', { domain: 'sync', draftId: currentDraftId })
        return
      }

      if (errorType === 'serverError' || errorType === 'notFound') {
        // Backend rejected the patch outright — draft is likely gone (expired, session rotated,
        // or no longer in newOrder state). Clear locally; next cart operation will create
        // a fresh draft. Cart items stay — they live in Zustand, not the server draft.
        safeGetLogger()?.warn('Draft sync failed, clearing stale draft', {
          domain: 'sync', draftId: currentDraftId, errorType, requestPath, status, responseBody,
          error: String(error),
        })
        setDraftOrderId(null)
        return
      }

      if (errorType === 'validation' || errorType === 'network') {
        // validation: backend rejected a specific field value on an otherwise-healthy draft
        //   (e.g. a freshly-created delivery_address_id that fails draft-side validity).
        // network: transient connectivity failure; no response was received.
        // Both cases: don't clear the draft. The useEffect re-fires on the next tracked
        // dep change (merchantId | orderType | deliveryAddressId), which retries the sync.
        safeGetLogger()?.warn('Draft metadata sync rejected', {
          domain: 'sync', draftId: currentDraftId, errorType, requestPath, status, responseBody,
          error: String(error),
        })
        return
      }

      safeGetLogger()?.error('Draft metadata sync failed', {
        domain: 'sync', draftId: currentDraftId, errorType, requestPath, status, responseBody,
        error: String(error),
      })
    })
  }, [isAuthenticated, merchantId, orderType, deliveryAddressId, cartSyncService, createSyncContext, getPriceBreakdown])

  // Keep cartStore.taxRate in sync with the current location's taxRate.
  // Each location can have its own tax rate; fall back to restaurant-level rate
  // or the 8.75% platform default when neither is configured.
  useEffect(() => {
    if (isShowcase()) return
    if (!currentRestaurant) return

    // Prefer location-specific tax rate, fall back to restaurant-level
    const locationTaxRate = currentLocation?.taxRate ?? 0
    const effectiveTaxRate = locationTaxRate || (currentRestaurant.taxRate ?? 0)

    if (!effectiveTaxRate) {
      safeGetLogger()?.debug('No taxRate configured, using default', { domain: 'config', restaurantId: currentRestaurant?.id, locationId: currentLocation?.id })
    }

    setTaxRate(effectiveTaxRate)
  }, [currentRestaurant?.id, currentRestaurant?.taxRate, currentLocation?.id, currentLocation?.taxRate, setTaxRate])

  // Sync from backend on login/app load
  useEffect(() => {
    if (isShowcase()) return
    if (isAuthenticated && user?.id && currentRestaurant?.id && !hasSyncedRef.current) {
      hasSyncedRef.current = true
      syncFromBackend()
    }

    // Reset sync flag on logout
    if (!isAuthenticated) {
      hasSyncedRef.current = false
      hasRestoredRef.current = false
      pendingRestoreDraftRef.current = null
    }
  }, [isAuthenticated, user?.id, currentRestaurant?.id, syncFromBackend])

  // Restore cart from a pending draft when categories become available.
  // pendingRestoreDraftRef is populated by syncFromBackend when the local cart
  // is empty and the remote draft has items.  This effect fires either
  // immediately (categories already loaded) or deferred (categories load later).
  // draftOrderId is included as a dependency so the effect re-runs right after
  // syncFromBackend stores the draft and sets the ID — even if categories were
  // already populated before the fetch resolved.
  useEffect(() => {
    if (isShowcase()) return
    const draft = pendingRestoreDraftRef.current
    if (!draft || hasRestoredRef.current || categories.length === 0 || !currentRestaurant?.id) {
      return
    }

    // Build Map<string, MenuItem> by flattening all category items
    const menuItemMap = new Map<string, MenuItem>()
    for (const cat of categories) {
      for (const item of cat.items) {
        menuItemMap.set(item.id, item)
      }
    }

    // Convert DraftOrderItem[] (camelCase) → DraftItemInput[] (snake_case).
    // Strip is_hidden / display_order from each selected_item so the shape
    // matches DraftSelectedItem (Omit<BackendSelectedItem, 'is_hidden' | 'display_order'>).
    const draftItems = draft.items.map((item: DraftOrderItem) => ({
      item_id: item.itemId,
      menu_item_id: item.menuItemId,
      quantity: item.quantity,
      selected_options: item.selectedOptions.map((opt) => ({
        option_title: opt.option_title,
        selected_items: opt.selected_items.map(({ customization_item_id, name, additional_price }) => ({
          customization_item_id,
          name,
          additional_price,
        })),
      })),
      ...(item.specialInstructions !== undefined && {
        special_instructions: item.specialInstructions,
      }),
      ...(item.selectedPriceVariant !== undefined && {
        selected_price_variant: item.selectedPriceVariant,
      }),
    }))

    const { restoredItems, incompleteCount } = buildCartItemsFromDraft(draftItems, menuItemMap)
    const droppedCount = draftItems.length - restoredItems.length

    // Always call restoreFromDraft so restaurantId and draftOrderId are atomically
    // consistent — even when all draft items were dropped (empty restoredItems).
    safeGetLogger()?.sync('draft_restored', { itemCount: restoredItems.length })
    restoreFromDraft(restoredItems, currentRestaurant.id, draft.orderId)

    // Notify user if any items were unavailable or need review
    if (droppedCount > 0 || incompleteCount > 0) {
      platformService.showToast({
        message: 'Some items from your saved cart have been updated',
        type: 'warning',
      })
    }

    // One-shot: prevent re-restoration even if categories are refreshed later
    hasRestoredRef.current = true
    pendingRestoreDraftRef.current = null
  }, [categories, draftOrderId, restoreFromDraft, currentRestaurant?.id, platformService])

  return {
    syncFromBackend,
  }
}
